import { Award, Shield, HandHelping, Hammer, Facebook, ArrowRight } from "lucide-react";

const VALUES = [
  {
    icon: Hammer,
    title: "Hand-built by us",
    text: "No sub-crews. The same team that quotes your floor lays every plank, tile, and cut.",
  },
  {
    icon: Shield,
    title: "Engineered to last",
    text: "Level, moisture-sealed subfloor for every flooring install · engineered gravel base for every paver. No shortcuts.",
  },
  {
    icon: HandHelping,
    title: "We answer the phone",
    text: "Need us after install? Call. We'll pick up. That's how a family business works.",
  },
];

export default function OurStory() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* LEFT — Story */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-orange-600 mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />
              ABOUT M&amp;L
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
              A family business
              <br />
              <span className="text-stone-500">building beautiful floors
                <br />
                across St. Louis since 2013.
              </span>
            </h2>

            <div className="mt-10 space-y-5 text-stone-700 text-[17px] leading-[1.65] max-w-2xl">
              <p>
                M&amp;L Exterior and Interior Design started with a simple frustration:
                most flooring installs in Missouri fail within a few years
                because contractors cut corners on the subfloor prep.
              </p>
              <p>
                We do it differently. We're not a sales-first crew that subs
                out the actual install. Every laminate, LVP, and tile floor —
                and every paver patio we take on — is built by our own team
                on a properly prepped foundation that won't shift, crack, or
                lift.
              </p>
              <p>
                Twelve years in, our work still looks the day it was laid.
                That's the standard. That's the promise.
              </p>
            </div>

            {/* Inline credentials */}
            <div className="mt-10 inline-flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-full px-5 py-3">
              <Award size={16} className="text-orange-600" />
              <span className="text-sm font-semibold text-stone-800">
                Licensed · Insured · ICPI Certified · Family-Owned
              </span>
            </div>

            {/* Facebook follow CTA */}
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61589883083004"
                target="_blank"
                rel="noreferrer"
                data-testid="about-facebook-cta"
                className="inline-flex items-center gap-3 px-5 py-3 bg-[#1877F2] hover:bg-[#0f5fc7] text-white text-sm font-semibold rounded-full transition-colors shadow-[0_6px_22px_-8px_rgba(24,119,242,0.55)] group self-start"
                aria-label="See our latest work on Facebook"
              >
                <Facebook size={16} className="fill-white" />
                See our latest work on Facebook
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
              <span className="text-sm text-stone-500 max-w-xs">
                Fresh project photos & customer reviews posted regularly.
              </span>
            </div>
          </div>

          {/* RIGHT — Values + Quote */}
          <div className="lg:col-span-5">
            <div className="space-y-3">
              {VALUES.map((v) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.title}
                    className="flex items-start gap-5 bg-stone-50 rounded-2xl p-6 border border-stone-200"
                  >
                    <div className="w-12 h-12 bg-stone-900 text-stone-50 rounded-xl flex items-center justify-center shrink-0">
                      <Icon size={18} strokeWidth={1.75} />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-xl text-stone-900 leading-tight">
                        {v.title}
                      </div>
                      <p className="mt-1.5 text-stone-600 text-[15px] leading-relaxed">
                        {v.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 bg-stone-900 text-stone-100 rounded-2xl p-7 relative overflow-hidden">
              <div
                aria-hidden
                className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-25 blur-[60px] pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(234,88,12,0.8) 0%, rgba(234,88,12,0) 70%)",
                }}
              />
              <div className="relative">
                <div className="text-xs uppercase tracking-wider text-orange-400 font-semibold mb-3">
                  Our Promise
                </div>
                <p className="font-display italic text-2xl md:text-[26px] leading-snug">
                  "If a plank lifts, a tile cracks, or a paver shifts on a job
                  we built, we come back. No fine print. No fees."
                </p>
                <div className="mt-5 text-stone-400 text-sm">
                  — The M&amp;L Team
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
